import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircleIcon, MailIcon, RefreshCwIcon, AlertCircleIcon, ArrowLeftIcon, LoaderIcon, ShieldIcon } from 'lucide-react';
export const TeacherEmailVerificationPage = () => {
  const {
    currentUser,
    sendVerificationEmail,
    refreshUser
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Get the redirect path from location state
  const from = location.state?.from?.pathname || '/teacher-dashboard';
  // FIXED: Enhanced verification check with better debugging
  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (!currentUser) {
        console.log('No current user found, skipping verification check');
        return;
      }
      try {
        // Only show loading indicator when manually checking
        if (!checkingVerification) {
          setLoading(true);
        }
        console.log('Checking verification status for:', currentUser.email);
        console.log('Current verification status:', currentUser.emailVerified);
        await refreshUser();
        console.log('User refreshed, updated verification status:', currentUser.emailVerified);
        if (currentUser.emailVerified) {
          console.log('Email is verified, will redirect to:', from);
          setIsVerified(true);
          // Redirect after a short delay to show the success message
          setTimeout(() => navigate(from, {
            replace: true
          }), 1500);
        } else {
          console.log('Email is not verified yet');
          // FIXED: Send verification email automatically if one hasn't been sent yet
          if (!verificationSent && countdown === 0) {
            try {
              console.log('Sending initial verification email automatically');
              await sendVerificationEmail();
              setVerificationSent(true);
              console.log('Verification email sent automatically');
              // Set countdown to prevent multiple automatic sends
              setCountdown(60);
            } catch (emailError: any) {
              console.error('Error sending initial verification email:', emailError);
              console.error('Error code:', emailError.code);
              console.error('Error message:', emailError.message);
              // Don't show error for the automatic attempt
            }
          }
        }
      } catch (error: any) {
        console.error('Error checking verification status:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
      } finally {
        setLoading(false);
        setCheckingVerification(false);
      }
    };
    checkVerificationStatus();
    // Set up an interval to periodically check verification status
    // Check every 10 seconds instead of 5 to reduce API calls
    const intervalId = setInterval(checkVerificationStatus, 10000);
    return () => clearInterval(intervalId);
  }, [currentUser, refreshUser, navigate, from, verificationSent, countdown, sendVerificationEmail, checkingVerification]);
  // FIXED: Enhanced resend verification with better error handling
  const handleResendVerification = async () => {
    if (countdown > 0) return;
    setLoading(true);
    setError('');
    try {
      console.log('Manually resending verification email');
      await sendVerificationEmail();
      console.log('Verification email sent successfully');
      setVerificationSent(true);
      // Set countdown for 60 seconds
      setCountdown(60);
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      // More specific error messages based on error codes
      if (error.code === 'auth/too-many-requests') {
        setError('Too many verification emails sent. Please try again later.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Failed to send verification email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  // FIXED: Enhanced manual refresh with better error handling
  const handleManualRefresh = async () => {
    setCheckingVerification(true);
    setError('');
    try {
      console.log('Manually refreshing user verification status');
      await refreshUser();
      console.log('User refreshed, verification status:', currentUser?.emailVerified);
      if (currentUser?.emailVerified) {
        setIsVerified(true);
        console.log('Email is verified, will redirect to:', from);
        // Redirect after a short delay to show the success message
        setTimeout(() => navigate(from, {
          replace: true
        }), 1500);
      } else {
        console.log('Email is still not verified after manual refresh');
        setError('Your email is not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error: any) {
      console.error('Error refreshing user:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      // More specific error messages based on error codes
      if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Failed to refresh verification status. Please try again.');
      }
    } finally {
      setCheckingVerification(false);
    }
  };
  // If user is not logged in, redirect to login
  useEffect(() => {
    if (!currentUser && !loading) {
      console.log('No user logged in, redirecting to login');
      navigate('/teacher-login');
    }
  }, [currentUser, loading, navigate]);
  return <div className="min-h-screen flex flex-col bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-6368">
      <div className="flex-grow flex items-center justify-center px-4 py-12" data-id="element-6369">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 max-w-md w-full border border-white/20 shadow-xl" data-id="element-6370">
          <div className="text-center mb-6" data-id="element-6371">
            {isVerified ? <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" data-id="element-6372" /> : <MailIcon className="h-16 w-16 text-deep-pink mx-auto mb-4" data-id="element-6373" />}
            <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-6374">
              {isVerified ? 'Email Verified!' : 'Verify Your Email'}
            </h1>
            {!isVerified && currentUser && <p className="text-gray-300 text-sm" data-id="element-6375">
                We've sent a verification link to{' '}
                <span className="font-medium text-white" data-id="element-6376">
                  {currentUser?.email}
                </span>
              </p>}
          </div>
          {isVerified ? <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-6" data-id="element-6377">
              <p className="text-white text-center" data-id="element-6378">
                Your email has been verified successfully. You will be
                redirected to your dashboard.
              </p>
            </div> : <>
              {error && <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center" data-id="element-6379">
                  <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" data-id="element-6380" />
                  <p className="text-white text-sm" data-id="element-6381">{error}</p>
                </div>}
              {verificationSent && !error && <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-6 flex items-center" data-id="element-6382">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" data-id="element-6383" />
                  <p className="text-white text-sm" data-id="element-6384">
                    Verification email sent! Please check your inbox and spam
                    folder.
                  </p>
                </div>}
              <div className="space-y-6" data-id="element-6385">
                <div className="bg-white/5 rounded-lg p-4 border border-white/10" data-id="element-6386">
                  <h3 className="font-medium text-white mb-2" data-id="element-6387">
                    What to do next:
                  </h3>
                  <ol className="list-decimal list-inside text-gray-300 space-y-2 text-sm" data-id="element-6388">
                    <li data-id="element-6389">Check your email inbox for the verification link</li>
                    <li data-id="element-6390">If you don't see it, check your spam or junk folder</li>
                    <li data-id="element-6391">Click the verification link in the email</li>
                    <li data-id="element-6392">
                      Return to this page and click "I've Verified My Email"
                    </li>
                  </ol>
                </div>
                {/* Add a new troubleshooting section */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/10" data-id="element-6393">
                  <div className="flex items-center mb-2" data-id="element-6394">
                    <ShieldIcon className="h-4 w-4 text-deep-pink mr-2" data-id="element-6395" />
                    <h3 className="font-medium text-white" data-id="element-6396">
                      Troubleshooting Tips:
                    </h3>
                  </div>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 text-sm" data-id="element-6397">
                    <li data-id="element-6398">
                      Make sure to click the link in the most recent
                      verification email
                    </li>
                    <li data-id="element-6399">
                      Try copying the verification link and opening it in a new
                      browser tab
                    </li>
                    <li data-id="element-6400">
                      If using a mobile device, try on a desktop computer
                      instead
                    </li>
                    <li data-id="element-6401">
                      Check that you're using the same browser you signed up
                      with
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col space-y-3" data-id="element-6402">
                  <button onClick={handleManualRefresh} disabled={loading || checkingVerification} className="bg-white/20 hover:bg-white/30 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center" data-id="element-6403">
                    {checkingVerification ? <LoaderIcon className="h-5 w-5 animate-spin mr-2" data-id="element-6404" /> : <RefreshCwIcon className="h-5 w-5 mr-2" data-id="element-6405" />}
                    I've Verified My Email
                  </button>
                  <button onClick={handleResendVerification} disabled={loading || countdown > 0} className="bg-deep-pink hover:bg-burgundy disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 px-6 rounded-lg transition-colors flex items-center justify-center" data-id="element-6406">
                    {loading && !checkingVerification ? <LoaderIcon className="h-5 w-5 animate-spin mr-2" data-id="element-6407" /> : <MailIcon className="h-5 w-5 mr-2" data-id="element-6408" />}
                    {countdown > 0 ? `Resend Email (${countdown}s)` : 'Resend Verification Email'}
                  </button>
                </div>
              </div>
            </>}
          <div className="mt-8 text-center" data-id="element-6409">
            <Link to="/teacher-login" className="text-gray-300 hover:text-white text-sm flex items-center justify-center" data-id="element-6410">
              <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-6411" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>;
};